import { useEffect, useId, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import { myMembershipsQuery, useMe } from "../api/queries";
import { Icon } from "../design/components/Icon";
import { useT } from "../i18n/locale";
import { useOptionalAccess, useOptionalTab } from "../session/access";
import { switchTab } from "../session/switch-tab";
import "./company-switcher.css";

// Il menu delle aziende in cima alla rail (decisione del product owner del 3 ottobre 2026: «si mette in alto un menu a
// tendina dove puoi vedere tutte le aziende»). Dice in quale azienda lavora la scheda; chi lavora in più aziende, e il
// Super admin di Gioia365, la cambia da qui con un clic, senza passare da «Il tuo account». La scelta passa dal cambio di
// sempre (`switchTab`): la cache dell'azienda di prima si svuota e si arriva alla Home, e una bozza non salvata può
// fermarla. Il Super admin di Gioia365 trova anche «Tutte le aziende di Gioia365», da cui entra nelle altre.
// Con la rail a icone (`iconsOnly`) la voce mostra soltanto l'icona, con il tooltip nativo che dice l'azienda, e il menu si
// apre accanto all'icona senza aprire la rail (decisione del product owner del 4 ottobre 2026, `company-switcher.css`).
export function CompanySwitcher({ onChoose, iconsOnly = false }: { onChoose: () => void; iconsOnly?: boolean }) {
  const t = useT();
  const me = useMe();
  const access = useOptionalAccess();
  const tab = useOptionalTab();
  const router = useRouter();
  const memberships = useQuery(myMembershipsQuery).data ?? [];
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Aperto, si chiude con Esc (il focus torna al pulsante) o con un clic fuori dal menu.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const root = rootRef.current;
    root?.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      root?.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [open]);

  const current = access?.context.organization.name ?? null;
  const canSwitch = memberships.length > 1 || me.isPlatformAdmin;
  if (current === null && !canSwitch) return null;

  const label = t("shell.company.button", { organization: current ?? t("shell.company.none") });
  // Una sola azienda: il nome, senza menu.
  if (!canSwitch) {
    return (
      <div className="g-company">
        <span className="g-nav__item g-company__static" title={iconsOnly ? label : undefined}>
          <Icon name="building" size="md" />
          <span className="g-nav__label">
            <span className="g-visually-hidden">{t("shell.company.label")}</span>
            <span className="g-company__name">{current}</span>
          </span>
        </span>
      </div>
    );
  }

  async function choose(membershipId: string) {
    setOpen(false);
    onChoose();
    await switchTab(router, { kind: "membership", membershipId });
  }

  return (
    <div className="g-company" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="g-nav__item g-company__button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={label}
        title={iconsOnly ? label : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon name="building" size="md" />
        <span className="g-nav__label">
          <span className="g-company__name">{current ?? t("shell.company.none")}</span>
        </span>
      </button>
      {open ? (
        <div id={panelId} className="g-company__panel">
          {/* Il Super admin di Gioia365 entrato in un'azienda che non è fra le sue: è lei l'azienda aperta nella scheda. */}
          {tab?.kind === "platform" && current !== null ? (
            <button type="button" className="g-company__option" aria-current="true" onClick={() => setOpen(false)}>
              <span className="g-company__option-name">{current}</span>
              <span className="g-company__option-role">{t("shell.company.currentRole", { role: t("shell.company.platformRole") })}</span>
            </button>
          ) : null}
          {memberships.length > 0 ? <p className="g-company__title">{t("shell.company.title")}</p> : null}
          {memberships.length > 0 ? (
            <ul className="g-company__list">
              {memberships.map((membership) => {
                const isCurrent = tab?.kind === "membership" && tab.membershipId === membership.membershipId;
                return (
                  <li key={membership.membershipId}>
                    <button
                      type="button"
                      className="g-company__option"
                      aria-current={isCurrent ? "true" : undefined}
                      onClick={() => (isCurrent ? setOpen(false) : void choose(membership.membershipId))}
                    >
                      <span className="g-company__option-name">{membership.organizationName}</span>
                      <span className="g-company__option-role">
                        {isCurrent ? t("shell.company.currentRole", { role: membership.roleName }) : membership.roleName}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
          {me.isPlatformAdmin ? (
            <Link
              to="/gioia365/aziende"
              className="g-company__all"
              onClick={() => {
                setOpen(false);
                onChoose();
              }}
            >
              {t("shell.company.all")}
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
